import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { FilePickerDialogProps, FilePickerSource, FilePickerTab } from "./file-picker/types"
import type { GraphFileItem } from "@/api/models"

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { logger } from "@/lib/logger"
import { cn } from "@/lib/utils"

// Refactored components and hooks
import { FilePickerErrorState } from "./file-picker/components/error-state"
import { FileListWithHeader } from "./file-picker/components/file-list-with-header"
import { FilePickerFooter } from "./file-picker/components/file-picker-footer"
import { LoadingState } from "./file-picker/components/loading-state"
import { LocalFileUpload } from "./file-picker/components/local-file-upload"
import { SharePointLibraries } from "./file-picker/components/sharepoint-libraries"
import { SharePointSites } from "./file-picker/components/sharepoint-sites"
import { useFilePickerData } from "./file-picker/hooks/use-file-picker-data"
import { useFilePickerNavigation } from "./file-picker/hooks/use-file-picker-navigation"
import { useFilePickerSelection } from "./file-picker/hooks/use-file-picker-selection"
import { useFilePickerSorting } from "./file-picker/hooks/use-file-picker-sorting"

// Re-export types for backward compatibility
export type { FilePickerSource } from "./file-picker/types"

/**
 * Helper function to check if a file matches the accept filter
 * @param file - The file to check
 * @param accept - Accept filter object (e.g., { 'image/*': ['.jpg', '.png'] })
 * @returns true if file matches any of the accept criteria
 */
function matchesAcceptFilter(
  file: { name: string; mimeType: string; isFolder?: boolean },
  accept: Record<string, string[]>
): boolean {
  // Always include folders for navigation
  if (file.isFolder) {
    return true
  }

  // Get file extension
  const fileExtension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] || ""

  // Check each accept rule
  for (const [mimePattern, extensions] of Object.entries(accept)) {
    // Check MIME type pattern (e.g., "image/*" matches "image/png")
    if (mimePattern.includes("*")) {
      const prefix = mimePattern.split("*")[0]
      if (file.mimeType.startsWith(prefix)) {
        return true
      }
    } else if (file.mimeType === mimePattern) {
      return true
    }

    // Check file extensions
    if (extensions.some((ext) => fileExtension === ext.toLowerCase())) {
      return true
    }
  }

  return false
}

export function FilePickerDialog({
  open,
  source = "recent",
  onOpenChange,
  onSelect,
  onDocumentsSelected,
  onLocalFilesSelected,
  isNavigator = false,
  accept,
  selectionMode = "multiple",
  allowedSources,
  localFormatsHint,
  onLocalFilesRejected,
}: FilePickerDialogProps) {
  const { t } = useTranslation()
  const [isUploading, setIsUploading] = useState(false)

  // Custom hooks
  const navigation = useFilePickerNavigation(source)
  const selection = useFilePickerSelection()
  const sorting = useFilePickerSorting()
  const { files, documents, sites, libraries, isLoading, error } = useFilePickerData({
    tab: navigation.tab,
    open,
    navStack: navigation.navStack,
    selectedSite: navigation.selectedSite,
    selectedLibrary: navigation.selectedLibrary,
  })
  const documentItems = useMemo<GraphFileItem[]>(() => {
    return documents.map((document) => ({
      id: document.id,
      name: document.title || "Untitled document",
      webUrl: "",
      size: document.content.length,
      lastModified: document.updated ?? document.created ?? "",
      driveId: "",
      siteId: "",
      siteUrl: "",
      path: "",
      mimeType: "text/markdown",
      isFolder: false,
    }))
  }, [documents])

  const effectiveAllowedSources = useMemo<FilePickerSource[]>(
    () =>
      allowedSources && allowedSources.length > 0
        ? allowedSources
        : ["recent", "local", "documents", "oneDrive", "sharePoint", "sharedWithMe"],
    [allowedSources]
  )

  // Reset navigation and loading state when dialog opens or source changes
  useEffect(() => {
    if (!open) {
      setIsUploading(false)
      return
    }
    // In navigator mode, always default to local tab when available.
    const preferredTab: FilePickerSource = isNavigator ? "local" : source
    const defaultTab = effectiveAllowedSources.includes(preferredTab)
      ? preferredTab
      : effectiveAllowedSources[0] || "local"
    navigation.setTab(defaultTab)
    navigation.resetNavigation()
    selection.clearSelection()
    selection.clearLocalFiles()
    setIsUploading(false)
  }, [source, open, isNavigator, effectiveAllowedSources])

  const allTabs: FilePickerTab[] = [
    {
      key: "recent",
      label: t("filepicker.recent", { defaultValue: "Recent" }),
    },
    {
      key: "local",
      label: t("filepicker.local", { defaultValue: "Local" }),
    },
    {
      key: "documents",
      label: t("filepicker.documents", { defaultValue: "Documents" }),
    },
    {
      key: "oneDrive",
      label: t("filepicker.my_files", { defaultValue: "OneDrive" }),
    },
    {
      key: "sharePoint",
      label: t("filepicker.sharepoint", { defaultValue: "SharePoint" }),
    },
    {
      key: "sharedWithMe",
      label: t("filepicker.shared_with_me", { defaultValue: "Shared" }),
    },
  ]
  const tabs = allTabs.filter((tab) => effectiveAllowedSources.includes(tab.key))

  const handleSelectAll = () => {
    if (selectionMode === "single") return
    const sourceFiles = navigation.tab === "documents" ? documentItems : files
    const sortedFiles = sorting.sortFiles(sourceFiles)
    selection.selectAll(sortedFiles)
  }

  const handleToggleSelection = (file: GraphFileItem) => {
    if (selectionMode === "single") {
      const isAlreadySelected = Boolean(selection.selectedMap[file.id])
      selection.clearSelection()
      if (!isAlreadySelected) {
        selection.toggleSelection(file)
      }
      return
    }
    selection.toggleSelection(file)
  }

  const handleAddLocalFiles = (pickedFiles: File[]) => {
    if (selectionMode === "single") {
      selection.clearLocalFiles()
      if (pickedFiles.length > 0) {
        selection.addLocalFiles([pickedFiles[0]])
      }
      return
    }
    selection.addLocalFiles(pickedFiles)
  }

  const handleConfirm = async () => {
    try {
      setIsUploading(true)

      if (navigation.tab === "local") {
        if (selection.localFiles.length > 0 && onLocalFilesSelected) {
          const filesToSend =
            selectionMode === "single" ? selection.localFiles.slice(0, 1) : selection.localFiles
          const didSelect = await onLocalFilesSelected(filesToSend)
          if (didSelect === false) {
            return
          }
          selection.clearLocalFiles()
        }
      } else if (navigation.tab === "documents") {
        if (onDocumentsSelected) {
          const selectedIds = new Set(selection.getSelectedFiles().map((item) => item.id))
          const selectedDocuments = documents.filter((document) => selectedIds.has(document.id))
          if (selectedDocuments.length > 0) {
            const docsToSend =
              selectionMode === "single" ? selectedDocuments.slice(0, 1) : selectedDocuments
            await onDocumentsSelected(docsToSend)
          }
        }
        selection.clearSelection()
      } else {
        const selectedFiles = selection.getSelectedFiles()
        if (selectedFiles.length > 0) {
          const filesToSend = selectionMode === "single" ? selectedFiles.slice(0, 1) : selectedFiles
          // Handle each file selection sequentially to maintain order
          for (const file of filesToSend) {
            await onSelect(file)
          }
          selection.clearSelection()
        }
      }

      onOpenChange(false)
    } catch (error) {
      // Keep dialog open on error so the user can retry
      toast.error(
        t("filepicker.upload_error", {
          defaultValue: "Failed to process selected files. Please try again.",
        })
      )
      logger.error(
        "File picker confirm failed",
        error instanceof Error ? error : new Error(String(error))
      )
    } finally {
      setIsUploading(false)
    }
  }

  const renderTabContent = (tabKey: string) => {
    if (tabKey === "local") {
      return (
        <LocalFileUpload
          accept={accept}
          acceptOnlyImages={isNavigator}
          files={selection.localFiles}
          formatsHint={localFormatsHint}
          onAddFiles={handleAddLocalFiles}
          onRejectFiles={onLocalFilesRejected}
          onRemoveFile={selection.removeLocalFile}
        />
      )
    }

    if (isLoading) {
      return <LoadingState />
    }

    if (error) {
      // Let the user step back out of a folder, library or site they cannot
      // read. The navigation hook's own back path covers OneDrive folders and
      // SharePoint folders/libraries; a failing site listing needs the site
      // cleared, which the hook does not treat as "back".
      let onBack: (() => void) | undefined
      if (navigation.canNavigateBack()) {
        onBack = navigation.navigateBack
      } else if (tabKey === "sharePoint" && navigation.selectedSite) {
        onBack = () => navigation.setSelectedSite(null)
      }
      return <FilePickerErrorState error={error} onBack={onBack} />
    }

    if (tabKey === "sharePoint" && !navigation.selectedSite) {
      return (
        <SharePointSites
          sites={sites}
          sortDir="asc"
          sortKey="name"
          onSelectSite={navigation.selectSite}
        />
      )
    }

    if (tabKey === "sharePoint" && navigation.selectedSite && !navigation.selectedLibrary) {
      return (
        <SharePointLibraries
          libraries={libraries}
          selectedSiteName={navigation.selectedSite.displayName}
          sortDir="asc"
          sortKey="name"
          onNavigateBack={() => navigation.setSelectedSite(null)}
          onSelectLibrary={navigation.selectLibrary}
        />
      )
    }

    // Show files list for all other cases
    const sourceFiles = tabKey === "documents" ? documentItems : files

    // Apply accept filter if provided (for image-only mode, etc.)
    const filteredFiles = accept
      ? sourceFiles.filter((file) => matchesAcceptFilter(file, accept))
      : sourceFiles
    const sortedFiles = sorting.sortFiles(filteredFiles)

    // Show empty state if no files match the filter
    if (filteredFiles.length === 0 && sourceFiles.length > 0) {
      return (
        <div className="flex h-full flex-col items-center justify-center p-8 text-center">
          <p className="text-muted-foreground text-sm">
            {t("filepicker.no_files_match_filter", {
              defaultValue: "No files match the selected filter in this location.",
            })}
          </p>
        </div>
      )
    }

    return (
      <FileListWithHeader
        breadcrumb={navigation.getBreadcrumb()}
        canNavigateBack={navigation.canNavigateBack()}
        files={sortedFiles}
        selectedMap={selection.selectedMap}
        selectionMode={selectionMode}
        sortDirection={sorting.sorting.direction}
        sortOption={sorting.sorting.option}
        tabKey={navigation.tab}
        onClearSelection={selection.clearSelection}
        onNavigateBack={navigation.navigateBack}
        onNavigateToFolder={navigation.navigateToFolder}
        onSelectAll={handleSelectAll}
        onSortChange={sorting.setSortOption}
        onToggleSelection={handleToggleSelection}
      />
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[85vh] w-[95vw] max-w-4xl flex-col overflow-hidden p-0 sm:h-[90vh] sm:w-full sm:max-w-5xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="bg-muted/30 border-b px-4 py-3 sm:px-6 sm:py-4">
          <DialogTitle className="text-lg font-semibold sm:text-xl">
            {t("filepicker.title", { defaultValue: "Select Files" })}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 flex-col overflow-hidden px-4 pt-3 sm:px-6 sm:pt-4">
          <Tabs
            className="flex h-full flex-col"
            value={navigation.tab}
            onValueChange={(v) => navigation.setTab(v as FilePickerSource)}
          >
            <TabsList className="bg-muted/50 flex h-auto min-h-[2.5rem] w-full shrink-0 flex-wrap gap-1 p-1 text-xs sm:grid sm:h-12 sm:grid-cols-6 sm:gap-0 sm:text-sm">
              {tabs.map((tab) => {
                const isDisabled = isNavigator && tab.key !== "local"
                return (
                  <TabsTrigger
                    key={tab.key}
                    disabled={isDisabled}
                    value={tab.key}
                    className={cn(
                      "data-[state=active]:bg-background data-[state=active]:text-foreground min-w-[30%] flex-1 px-2 py-1.5 text-xs font-medium transition-[color,background-color,box-shadow] data-[state=active]:shadow-sm sm:flex-none sm:px-4 sm:text-sm",
                      {
                        "cursor-not-allowed opacity-50 hover:opacity-50": isDisabled,
                      }
                    )}
                  >
                    {tab.label}
                  </TabsTrigger>
                )
              })}
            </TabsList>

            {tabs.map((tab) => (
              <TabsContent key={tab.key} className="flex-1 overflow-hidden" value={tab.key}>
                <div className="h-full overflow-auto px-1 pt-3 sm:px-2 sm:pt-6">
                  {renderTabContent(tab.key)}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>

        <FilePickerFooter
          isUploading={isUploading}
          localFilesCount={selection.localFiles.length}
          selectedFilesCount={selection.getSelectedCount()}
          tab={navigation.tab}
          onCancel={() => onOpenChange(false)}
          onConfirm={handleConfirm}
        />
      </DialogContent>
    </Dialog>
  )
}
